import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedTitle } from '@creanizer/types'
import { createIndexedDBStorage, type CustomValue } from '@creanizer/utils'

interface TitleStore {
  savedTitles: SavedTitle[]
  maxTitles: number
  savedStyles: CustomValue[]
  
  // アクション
  saveTitle: (title: SavedTitle) => Promise<void>
  deleteTitle: (id: string) => void
  updateTitle: (id: string, updates: Partial<SavedTitle>) => void
  exportAsText: () => void
  exportAsJSON: () => void
  importFromJSON: (jsonString: string) => void
  clearAll: () => void
  
  // カスタムスタイル管理
  addCustomStyle: (value: string, label: string) => Promise<void>
  useCustomStyle: (id: string) => Promise<CustomValue | undefined>
  deleteCustomStyle: (id: string) => void
}

const MAX_TITLES = 1000

export const useTitleStore = create<TitleStore>()(
  persist(
    (set, get) => ({
      savedTitles: [],
      maxTitles: MAX_TITLES,
      savedStyles: [],

      saveTitle: async (title) => {
        const { savedTitles } = get()
        if (savedTitles.length >= MAX_TITLES) {
          throw new Error(`保存できるタイトルは最大${MAX_TITLES}件までです`)
        }
        set({ savedTitles: [...savedTitles, title] })
      },

      deleteTitle: (id) => {
        set(state => ({
          savedTitles: state.savedTitles.filter(t => t.id !== id)
        }))
      },

      updateTitle: (id, updates) => {
        set(state => ({
          savedTitles: state.savedTitles.map(t => 
            t.id === id 
              ? { ...t, ...updates, updatedAt: new Date().toISOString() }
              : t
          )
        }))
      },

      exportAsText: () => {
        const { savedTitles } = get()
        if (savedTitles.length === 0) {
          alert('保存されたタイトルがありません。')
          return
        }

        const content = formatTitlesForExport(savedTitles)
        downloadTextFile(content, `saved_titles_${Date.now()}.txt`)
      },

      exportAsJSON: () => {
        const { savedTitles } = get()
        if (savedTitles.length === 0) {
          alert('保存されたタイトルがありません。')
          return
        }

        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'titles',
          data: savedTitles
        }

        downloadJSONFile(exportData, `saved_titles_${Date.now()}.json`)
      },

      importFromJSON: (jsonString: string) => {
        try {
          const importData = JSON.parse(jsonString)
          
          // バリデーション
          if (!importData.data && !importData.titles) {
            throw new Error('無効なデータ形式です')
          }

          const { maxTitles, savedTitles } = get()
          const newTitles = (importData.data || importData.titles) as SavedTitle[]
          
          // 重複チェック（タイトル名とスタイルで判定）
          const uniqueTitles = newTitles.filter(newTitle => {
            return !savedTitles.some(existing => 
              existing.title === newTitle.title && 
              existing.input.style === newTitle.input.style
            )
          })
          
          // マージ後の最大数チェック
          const totalCount = savedTitles.length + uniqueTitles.length
          if (totalCount > maxTitles) {
            const availableSlots = maxTitles - savedTitles.length
            if (availableSlots <= 0) {
              alert(`保存数が上限（${maxTitles}件）に達しています。不要なタイトルを削除してください。`)
              return
            }
            if (!confirm(`追加可能な件数は${availableSlots}件です。${uniqueTitles.length}件中${availableSlots}件のみインポートしますか？`)) {
              return
            }
            uniqueTitles.splice(availableSlots)
          }

          // IDの重複を避けるため、新しいIDを生成して追加
          const importedTitles = uniqueTitles.map(title => ({
            ...title,
            id: crypto.randomUUID(),
            createdAt: title.createdAt || new Date().toISOString(),
            updatedAt: title.updatedAt || new Date().toISOString()
          }))
          
          const mergedTitles = [...savedTitles, ...importedTitles]

          set({ savedTitles: mergedTitles })
          
          const skippedCount = newTitles.length - uniqueTitles.length
          alert(`${importedTitles.length}件のタイトルをインポートしました。${skippedCount > 0 ? `(${skippedCount}件の重複をスキップ)` : ''}`)
        } catch (error) {
          console.error('Import error:', error)
          alert('インポートに失敗しました。ファイル形式を確認してください。')
        }
      },

      clearAll: () => {
        if (confirm('保存されているすべてのタイトルを削除しますか？')) {
          set({ savedTitles: [] })
        }
      },

      addCustomStyle: async (value, label) => {
        const customValue: CustomValue = {
          id: crypto.randomUUID(),
          type: 'style',
          value,
          label,
          createdAt: new Date(),
          usageCount: 0,
          lastUsedAt: new Date(),
        }
        
        set(state => ({
          savedStyles: [...state.savedStyles, customValue]
        }))
      },

      useCustomStyle: async (id) => {
        const { savedStyles } = get()
        const style = savedStyles.find(s => s.id === id)
        
        if (style) {
          set(state => ({
            savedStyles: state.savedStyles.map(s =>
              s.id === id
                ? { 
                    ...s, 
                    lastUsedAt: new Date(),
                    usageCount: s.usageCount + 1
                  }
                : s
            )
          }))
        }
        
        return style
      },

      deleteCustomStyle: (id) => {
        set(state => ({
          savedStyles: state.savedStyles.filter(s => s.id !== id)
        }))
      },
    }),
    {
      name: 'title-store',
      storage: createIndexedDBStorage('title-store'),
      partialize: (state) => ({ savedTitles: state.savedTitles, maxTitles: state.maxTitles, savedStyles: state.savedStyles }),
      migrate: (persistedState: any, version: number) => {
        // バージョン0（初期状態）からバージョン1への移行
        if (version === 0) {
          // 古いデータ構造から新しいデータ構造への変換
          return persistedState
        }
        return persistedState
      }
    }
  )
)

// タイトルをテキスト形式にフォーマット
function formatTitlesForExport(titles: SavedTitle[]): string {
  const date = new Date().toLocaleString('ja-JP')
  
  let content = `【保存されたタイトル一覧】
生成日時: ${date}
総数: ${titles.length}件

`

  titles.forEach((title, index) => {
    content += `\n【${index + 1}】 ${title.title}\n`
    content += '=' .repeat(50) + '\n'
    
    // 生成パラメータ
    content += `\n生成パラメータ:\n`
    content += `  スタイル: ${title.input.style}\n`
    content += `  タイプ: ${title.input.titleType === 'work' ? '作品全体' : 
                      title.input.titleType === 'chapter' ? '章' : '各話'}\n`
    content += `  創造性レベル: ${title.input.creativityLevel}/10\n`
    content += `  出力数: ${title.input.outputCount}\n`
    
    // 候補リスト
    if (title.result.candidates && title.result.candidates.length > 0) {
      content += `\n候補タイトル:\n`
      title.result.candidates.forEach((candidate, i) => {
        content += `  ${i + 1}. ${candidate.title}`
        if (candidate.reading) {
          content += ` (${candidate.reading})`
        }
        content += '\n'
        if (candidate.description) {
          content += `     ${candidate.description}\n`
        }
      })
    }
    
    // メモとタグ
    if (title.memo) {
      content += `\nメモ: ${title.memo}\n`
    }
    
    if (title.tags && title.tags.length > 0) {
      content += `タグ: ${title.tags.join(', ')}\n`
    }
    
    content += '\n'
  })

  return content
}

// テキストファイルをダウンロード
function downloadTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// JSONファイルをダウンロード
function downloadJSONFile(data: any, filename: string) {
  const jsonString = JSON.stringify(data, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}